'use client'
import React, { createContext, useContext, useCallback, useState, useRef, useEffect } from 'react'

interface DrawerCoordinatorContextType {
  currentOpenId: string | null
  requestOpen: (id: string) => void
  requestClose: (id?: string) => void
  isOpen: (id: string) => boolean
  debug?: boolean
}

const DrawerCoordinatorContext = createContext<DrawerCoordinatorContextType | null>(null)

export function DrawerCoordinatorProvider({ 
  children, 
  debug = false 
}: { 
  children: React.ReactNode
  debug?: boolean 
}) {
  const [currentOpenId, setCurrentOpenId] = useState<string | null>(null)
  const registeredDrawers = useRef<Set<string>>(new Set())

  const requestOpen = useCallback((id: string) => {
    if (debug) {
      console.log('[DrawerCoordinator] requestOpen:', id, 'current:', currentOpenId)
    }
    
    // If another drawer is open, close it first
    if (currentOpenId && currentOpenId !== id) {
      if (debug) {
        console.log('[DrawerCoordinator] Closing drawer:', currentOpenId, 'to open:', id)
      }
    }
    
    setCurrentOpenId(id)
    registeredDrawers.current.add(id)
  }, [currentOpenId, debug])

  const requestClose = useCallback((id?: string) => {
    if (debug) {
      console.log('[DrawerCoordinator] requestClose:', id, 'current:', currentOpenId)
    }
    
    // If no id provided, close whatever is open
    // If id provided, only close if it matches the current open drawer
    if (!id || currentOpenId === id) {
      setCurrentOpenId(null)
    }
  }, [currentOpenId, debug])

  const isOpen = useCallback((id: string) => {
    return currentOpenId === id
  }, [currentOpenId])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      registeredDrawers.current.clear()
    }
  }, [])

  const value = {
    currentOpenId,
    requestOpen,
    requestClose,
    isOpen,
    debug
  }

  return (
    <DrawerCoordinatorContext.Provider value={value}>
      {children}
    </DrawerCoordinatorContext.Provider>
  )
}

export function useDrawerCoordinator() {
  const context = useContext(DrawerCoordinatorContext)
  
  // Return a dummy implementation if no provider exists
  // This ensures backward compatibility
  if (!context) {
    return {
      currentOpenId: null,
      requestOpen: (_id: string) => {},
      requestClose: (_id?: string) => {},
      isOpen: (_id: string) => true, // Always return true if no coordinator
      debug: false
    }
  }
  
  return context
}

// Hook for components to register themselves
export function useCoordinatedDrawer(id?: string) {
  const coordinator = useDrawerCoordinator()
  const generatedId = React.useId()
  const drawerId = id || generatedId
  
  // If no ID provided (undefined), don't coordinate
  // This allows components to opt-out of coordination
  const shouldCoordinate = id !== undefined
  
  // Register on mount, unregister on unmount (only if coordinating)
  useEffect(() => {
    if (!shouldCoordinate) return
    
    if (coordinator.debug) {
      console.log('[DrawerCoordinator] Drawer registered:', drawerId)
    }
    
    return () => {
      // Clean up: if this drawer is open when unmounting, close it
      if (coordinator.isOpen(drawerId)) {
        coordinator.requestClose(drawerId)
      }
      if (coordinator.debug) {
        console.log('[DrawerCoordinator] Drawer unregistered:', drawerId)
      }
    }
  }, [drawerId, coordinator, shouldCoordinate])
  
  // If not coordinating, return undefined for open state
  if (!shouldCoordinate) {
    return {
      open: undefined,
      setOpen: () => {},
      drawerId: undefined
    }
  }
  
  const open = coordinator.isOpen(drawerId)
  
  const setOpen = useCallback((value: boolean) => {
    if (value) {
      coordinator.requestOpen(drawerId)
    } else {
      coordinator.requestClose(drawerId)
    }
  }, [coordinator, drawerId])
  
  return {
    open,
    setOpen,
    drawerId
  }
}